import React, { useState } from 'react';
import { Modal, Input, Alert, Typography } from 'antd';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { displayEmail } from '../utils/displayEmail';

const { Text } = Typography;

/**
 * Edit a Lead Advisor or Administrator (platform admin only): rename (PATCH /api/v1/users/:id) or remove
 * from the application (DELETE /api/v1/users/:id, revokes the Entra app role).
 * Email and role are sign-in identity and permission, so only the display name is editable here.
 */
const EditUserNameModal = ({ user, onClose, onSaved, onRemoved }) => {
    const { instance } = useMsal();
    const [name, setName] = useState(user.name || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [confirmingRemove, setConfirmingRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    const trimmed = name.trim();
    const unchanged = trimmed === (user.name || '');

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
            const token = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/users/${user.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.accessToken}` },
                body: JSON.stringify({ name: trimmed }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                throw new Error(json.error?.message || `Update failed (${res.status})`);
            }
            onSaved(json.data?.warnings || []);
        } catch (e) {
            setError(e.message || 'Update failed.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setRemoving(true);
        setError(null);
        try {
            const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
            const token = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/users/${user.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token.accessToken}` },
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                throw new Error(json.error?.message || `Removal failed (${res.status})`);
            }
            onRemoved(user);
        } catch (e) {
            setError(e.message || 'Removal failed.');
            setConfirmingRemove(false);
        } finally {
            setRemoving(false);
        }
    };

    return (
        <Modal
            open
            title="Edit user"
            okText="Save"
            onOk={save}
            onCancel={onClose}
            confirmLoading={saving}
            okButtonProps={{ disabled: !trimmed || unchanged }}
            destroyOnClose
        >
            <div className="space-y-4">
                <div>
                    <Text type="secondary">Email</Text>
                    <div className="font-medium">{displayEmail(user.email)}</div>
                </div>
                <div>
                    <Text type="secondary">Name</Text>
                    <Input
                        value={name}
                        maxLength={120}
                        onChange={(e) => setName(e.target.value)}
                        onPressEnter={() => trimmed && !unchanged && !saving && save()}
                        autoFocus
                    />
                </div>
                {error && <Alert type="error" showIcon message={error} />}
                <div className="border-t border-[color:var(--color-border)] pt-4">
                    {confirmingRemove ? (
                        <div className="space-y-3">
                            <Alert
                                type="warning"
                                showIcon
                                message={`Remove ${user.name} from the application? They will lose access immediately. Their history stays on record, and they can be invited again later.`}
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={remove}
                                    disabled={removing}
                                    className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] disabled:opacity-60"
                                >
                                    {removing ? 'Removing...' : 'Yes, remove'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConfirmingRemove(false)}
                                    disabled={removing}
                                    className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-border)] text-[color:var(--color-text-muted)]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setConfirmingRemove(true)}
                            className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-danger-line)] text-[color:var(--color-danger-fg)] hover:bg-[color:var(--color-danger-bg)] transition-all"
                        >
                            Remove from application
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default EditUserNameModal;
